import {
  Application,
  utils,
  Graphics,
  Sprite,
  Point,
  WRAP_MODES,
  filters,
  Container,
  BLEND_MODES,
  Text,
} from 'pixi.js';
import MouseTrail from './MouseTrail';
import WaterEffect from './WaterEffect';
import cursor from '@/scripts/cursor';
import engine from '@/scripts/engine';
import store from '@/store';

class Background {
  constructor(DOMContainer, DOMCanvas) {
    this.loaded = false;
    this.DOMContainer = DOMContainer;
    this.DOMCanvas = DOMCanvas;
    this.slides = [{
      name: 'live',
      video: 'video-uno',
      sprite: null,
      label: 'Live.',
      text: null,
      DOMContainer: document.querySelector('.slide[data-slide="live"]'),
      DOMReference: document.querySelector('.slide[data-slide="live"] h2'),
      DOMBlock: document.querySelector('.slide[data-slide="live"] .slide__block'),
    }, {
      name: 'your',
      video: 'video-due',
      sprite: null,
      label: 'Your.',
      text: null,
      DOMContainer: document.querySelector('.slide[data-slide="your"]'),
      DOMReference: document.querySelector('.slide[data-slide="your"] h2'),
      DOMBlock: document.querySelector('.slide[data-slide="your"] .slide__block'),
    }, {
      name: 'moment',
      video: 'video-tre',
      sprite: null,
      label: 'Moment.',
      text: null,
      DOMContainer: document.querySelector('.slide[data-slide="moment"]'),
      DOMReference: document.querySelector('.slide[data-slide="moment"] h2'),
      DOMBlock: document.querySelector('.slide[data-slide="moment"] .slide__block'),
    }, {
      name: 'prosecco',
      video: 'video-quattro',
      sprite: null,
      label: 'Prosecco.',
      text: null,
      DOMContainer: document.querySelector('.slide[data-slide="prosecco"]'),
      DOMReference: document.querySelector('.slide[data-slide="prosecco"] h2'),
      DOMBlock: document.querySelector('.slide[data-slide="prosecco"] .slide__block'),
    }];
    this.videoMap = new Map();

    utils.skipHello();
    this.app = new Application({
      resolution: Math.min(devicePixelRatio, 1.5),
      antialias: true,
      view: this.DOMCanvas,
      resizeTo: this.DOMContainer,
      backgroundColor: 0x1ECAD3,
      transparent: true,
    });

    window.addEventListener('resize', () => { this.resize(); });
  }

  load() {
    const videos = [
      { name: 'video-uno', url: '/static/video/01.mp4' },
      { name: 'video-due', url: '/static/video/02.mp4' },
      { name: 'video-tre', url: '/static/video/03.mp4' },
      { name: 'video-quattro', url: '/static/video/04.mp4' },
    ];
    this.app.loader.add('mouse-water', '/static/textures/mouse-water.png');
    this.app.loader.add('transition-water', '/static/textures/transition-water.jpg');
    this.app.loader.add('bottle-displacement', '/static/textures/bottle-displacement.png');
    this.app.loader.add('text-displacement', '/static/textures/text-displacement.png');
    const videoPromises = [];
    videos.forEach((video) => {
      const element = document.createElement('video');
      element.crossOrigin = 'anonymous';
      element.preload = '';
      element.src = video.url;
      element.muted = true;
      element.autoplay = false;
      element.setAttribute('playsinline', true);
      element.loop = true;
      video.element = element;
      videoPromises.push(new Promise(resolve => element.addEventListener('loadeddata', resolve)));
      element.load();
    });

    const loaderPromise = new Promise((resolve) => {
      this.app.loader.load(() => {
        // Save video in the video map
        videos.forEach((video) => {
          this.videoMap.set(video.name, video.element);
        });
        resolve();
      });
    });

    return Promise.all([...videoPromises, loaderPromise]);
  }

  init() {
    this.createVideos();
    this.createBackground();
    this.createTexts();
    this.createEffects();

    this.videoContainer.filters = [
      this.mouseWaterEffect,
      this.transitionWaterEffect,
      this.bottleDistortion,
    ];
    this.textsContianer.filters = [
      this.textDistortionEffect,
    ];

    this.resize();

    this.loaded = true;
  }

  render() {
    this.slides.forEach((slide) => {
      const referenceRect = slide.DOMReference.getBoundingClientRect();
      slide.text.x = referenceRect.left;
      slide.text.y = referenceRect.top;
      slide.text.alpha = window.getComputedStyle(slide.DOMBlock).opacity * window.getComputedStyle(slide.DOMContainer).opacity;
      slide.text.style.fontSize = parseFloat(window.getComputedStyle(slide.DOMReference).fontSize);
      slide.text.dirty = true;
    });
    this.mouseWaterEffect.speed = {
      x: cursor.ref.velocity.x,
      y: cursor.ref.velocity.y,
    };
    this.transitionWaterEffect.scale = {
      x: this.transitionWaterEffectScale.x,
      y: this.transitionWaterEffectScale.y,
    };
    this.bottleDistortion.scale = {
      x: this.bottleDistortionEffectScale.x,
      y: this.bottleDistortionEffectScale.y,
    };
    this.textDistortionEffect.scale = {
      x: this.textDistortionEffectScale.x,
      y: this.textDistortionEffectScale.y,
    };
  }

  resize() {
    this.slides.forEach(slide => this.setCover(slide.sprite));
  }

  prepareSlide(type) {
    this.current.sprite.alpha = 0;
    this.current.sprite.zIndex = 2;
    if (type === 'next') this.prev.sprite.zIndex = 1;
    else this.next.sprite.zIndex = 1;
    this.videoContainer.sortChildren();
    this.current.sprite.position.x += type === 'next' ? window.innerWidth / 4 : -window.innerWidth / 4;
    this.current.sprite.texture.baseTexture.resource.source.play();
    this.current.sprite.renderable = true;
  }

  disposeSlide(type) {
    if (type === 'next') {
      this.prev.sprite.renderable = false;
      this.prev.sprite.zIndex = 1;
      // this.prev.sprite.texture.baseTexture.resource.source.pause();
    } else {
      this.next.sprite.renderable = false;
      this.next.sprite.zIndex = 1;
      // this.next.sprite.texture.baseTexture.resource.source.pause();
    }
    this.videoContainer.sortChildren();
  }

  setCover(sprite) {
    if (!sprite) return;
    const texture = sprite.texture.baseTexture;
    const ratio = this.app.renderer.height / texture.height;
    sprite.scale = new Point(ratio, ratio);
    sprite.anchor = new Point(0.5, 0.5);
    sprite.position.x = this.app.renderer.width / 2;
    sprite.position.y = this.app.renderer.height / 2;

    this.backgroundColor.width = this.app.renderer.width;
    this.backgroundColor.height = this.app.renderer.height;
  }

  createVideos() {
    this.videoContainer = new Container();
    this.videoContainer.sortableChildren = true;
    this.slides.forEach((slide, index) => {
      slide.sprite = Sprite.from(this.videoMap.get(slide.video));
      this.videoContainer.addChild(slide.sprite);
      // slide.sprite.texture.baseTexture.resource.source.pause();
      slide.sprite.renderable = false;
      slide.sprite.zIndex = 1;
      if (store.state.slider.current === index) {
        slide.sprite.zIndex = 2;
        slide.sprite.renderable = true;
        slide.sprite.texture.baseTexture.resource.source.play();
      }
    });
    this.app.stage.addChild(this.videoContainer);
  }

  createBackground() {
    this.backgroundColor = new Graphics();
    this.backgroundColor
      .beginFill(0x1ECAD3)
      .drawRect(0, 0, this.app.renderer.width, this.app.renderer.height)
      .endFill();
    this.backgroundColor.alpha = 0.5;
    this.backgroundColor.blendMode = BLEND_MODES.OVERLAY;
    this.app.stage.addChild(this.backgroundColor);
  }

  createTexts() {
    this.textsContianer = new Container();
    this.slides.forEach((slide) => {
      slide.text = new Text(slide.label, {
        fontFamily: 'GT Super Display',
        fontSize: 350 * (window.innerWidth / 1920),
        fontWeight: '300',
        fill: 0xffffff,
      });
      this.textsContianer.addChild(slide.text);
    });
    this.app.stage.addChild(this.textsContianer);
  }

  createEffects() {
    this.mouseTrail = new MouseTrail(this.app);
    this.mouseTrailSprite = new Sprite(this.mouseTrail.texture);
    this.app.stage.addChild(this.mouseTrailSprite);

    const bottleDistortionTexture = this.app.loader.resources['bottle-displacement'].texture;
    this.bottleDistortionSprite = new Sprite(bottleDistortionTexture);
    this.bottleDistortionSprite.anchor = new Point(0.5, 0.5);
    this.bottleDistortionSprite.x = this.app.renderer.width / 2;
    this.bottleDistortionSprite.y = this.app.renderer.height / 2;
    this.bottleDistortionSprite.width = window.innerHeight * 2;
    this.bottleDistortionSprite.height = window.innerHeight * 2;
    this.app.stage.addChild(this.bottleDistortionSprite);
    const mouseWaterTexture = this.app.loader.resources['mouse-water'].texture;
    mouseWaterTexture.baseTexture.wrapMode = WRAP_MODES.REPEAT;
    this.mouseWaterDisplacementSprite = new Sprite(mouseWaterTexture);
    this.app.stage.addChild(this.mouseWaterDisplacementSprite);
    const transitionWaterTexture = this.app.loader.resources['transition-water'].texture;
    transitionWaterTexture.baseTexture.wrapMode = WRAP_MODES.REPEAT;
    this.transitionWaterDisplacementSprite = new Sprite(transitionWaterTexture);
    this.app.stage.addChild(this.transitionWaterDisplacementSprite);
    const textDistortionTexture = this.app.loader.resources['text-displacement'].texture;
    textDistortionTexture.baseTexture.wrapMode = WRAP_MODES.REPEAT;
    this.textDistortionSprite = new Sprite(textDistortionTexture);
    this.textDistortionSprite.anchor = new Point(0.5, 0.5);
    this.textDistortionSprite.position.x = (window.innerWidth / 32) * 7;
    this.textDistortionSprite.position.y = (window.innerHeight / 18) * 13;
    this.app.stage.addChild(this.textDistortionSprite);

    this.bottleDistortion = new filters.DisplacementFilter(this.bottleDistortionSprite, 0);
    this.mouseWaterEffect = new WaterEffect(
      this.mouseWaterDisplacementSprite,
      this.mouseTrailSprite,
      { x: 0, y: 0 },
      100,
    );
    this.transitionWaterEffect = new filters.DisplacementFilter(
      this.transitionWaterDisplacementSprite,
      0,
    );
    this.textDistortionEffect = new filters.DisplacementFilter(
      this.textDistortionSprite,
      100,
    );
    this.transitionWaterEffectScale = { x: 0, y: 0 };
    this.bottleDistortionEffectScale = { x: 0, y: 0 };
    this.textDistortionEffectScale = { x: 0, y: 0 };
  }

  get current() {
    return this.slides[store.state.slider.current];
  }

  get prev() {
    let prev = this.slides[store.state.slider.current - 1];
    if (typeof prev === 'undefined') prev = this.slides[3]; // eslint-disable-line
    return prev;
  }

  get next() {
    let next = this.slides[store.state.slider.current + 1];
    if (typeof next === 'undefined') next = this.slides[0]; // eslint-disable-line
    return next;
  }

  play() {
    engine.add(() => { this.render(); }, 'background-render', true);
    this.mouseTrail.play();
    this.app.ticker.start();
  }

  pause() { // eslint-disable-line
    engine.remove('background-render');
    this.mouseTrail.pause();
    this.app.ticker.stop();
  }
}

export default Background;
